import { AppFromLabelFieldProps } from "@/common/form-control/AppFromLabelField";
import { UniqueIdentifier } from "@dnd-kit/core";
import {
  SelectProps,
  SwitchProps,
  SxProps,
  TextFieldProps,
  Theme,
} from "@mui/material";
import { ReactNode } from "react";
import {
  Control,
  FieldValues,
  FormState,
  RegisterOptions,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from "react-hook-form";
import {
  AppOptionType,
  CategoryTypeUploadEnum,
  IDataResponseModel,
  IPagingRequestModel,
  IUploadFile,
} from "./Common.type";
import { DatePickerProps } from "@mui/x-date-pickers";
import { AppComboboxProps } from "@/common/AppCombobox";
import { AppRadioGroupProps } from "@/common/AppRadioGroup";
import dayjs from "dayjs";
import {
  ISysTableAction,
  UseSysTableSelectorType,
} from "@/common/AppSysTable/SysTable.slice";

export interface ISysTableItem {
  id: string;
  // [key: string]: unknown;
}
export type ISysTableCloneItem<T extends ISysTableItem> = {
  _checkbox?: boolean;
  _disabled?: boolean;
} & T;

export type HandleGetListPropsType<
  T extends AppSysTableType = AppSysTableDefaultType
> = {
  filter: T["FilterModel"];
  handleGetListDefault: (
    apiUrl: string,
    queryParams: T["FilterModel"]
  ) => Promise<void>;
  handleSetList: (data: T["RowModel"][], totalCount: number) => void;
};

export interface IColumn<
  RowModel extends AppSysTableType["RowModel"] = ISysTableDefaultItem,
  FilterModel extends AppSysTableType["FilterModel"] = ISysTableDefaultFilterModel
> {
  /**
   * Tên field **Required** và unique
   * trường hop không group phải trùng với field model mà Api trả về để lấy giá trị cho cột
   */
  field: string;

  /**
   * Label tên trên header table của cột đó
   */
  label: string | ReactNode;

  /**
   * Render ra table những cell type cơ bản đã được định nghĩa sẵn có
   */
  type?: TypeDisplayEnum;

  /**
   * Đối với type=InputPointText **bắt buộc** phải truyền list giá trị được nhập
   *
   * Ví dụ: ["T", "Đ", "C"]
   */
  valuePointText?: string[];

  /**
   * Đối với loại InputToggle **bắt buộc** phải truyền urlPatchStatus là endpoint của api url method **PATCH**
   *
   * Thay đổi giá trị của Toggle ngay trên grid table
   */
  urlPatchStatus?: string;

  /**
   * Dùng trong trường hợp nếu muốn ẩn cột không hiển thị trên table
   */
  isHideGrid?: boolean;

  /**
   * Cho phép người dùng cấu hình cột hiện tại trong popup ẩn/hiện .
   *
   * *Dùng trong trường hợp isCanSetupColumn=true*
   */
  isHideSetupColumn?: boolean;

  /**
   * - Cấu hình riêng cho cả cột
   */
  tableProps?: {
    /**
     * - className cho table header dùng để custom css header của cột
     */
    headerClassName?: string;

    /**
     * - custom render ô cell trong table nếu không mặc định render ra value
     *  - *Param value: la 1 item object row*
     */
    renderCellGrid?: (
      value: RowModel,
      index: number,
      useSysTableSelector: UseSysTableSelectorType<RowModel, FilterModel>,
      sysTableActions: ISysTableAction<RowModel, FilterModel>
    ) => string | ReactNode;

    /**
     * - className dùng để custom css cell (**chỉ hoạt động khi renderCellGrid=undefined**)
     */
    cellClassName?: string;

    /**
     * - Set width của cột
     */
    size?: number;

    /**
     * - Set minWidth của cột
     */
    minSize?: number;

    /**
     * - Set maxWidth của cột
     */
    maxSize?: number;

    /**
     * - Đối với type=inputSelect truyền options là các giá trị của select để người dùng chọn
     *
     * *Nếu truyền cả options và getOptions sẽ ưu tiên lấy giá trị options*
     */
    options?: AppOptionType[];

    /**
     * - Đối với type=inputSelect truyền getOptions là hàm xử lý để call api lấy giá trị cho select
     *
     * *Nếu truyền cả options và getOptions sẽ ưu tiên lấy giá trị options*
     */
    getOptions?: () => Promise<AppOptionType[]>;
  };

  /**
   * - Dùng trong trường hợp groupHeader
   *
   * *Khi truyền children cho column sẽ phải tạo ra một field là với value là **unique** không trùng với field model mà Api trả về*
   */
  children?: IColumn<RowModel>[];
}
export interface IColumnDrag extends IColumn {
  id: UniqueIdentifier;
}
export enum TypeInputEnum {
  Text,
  Textarea,
  Toggle,
  Number,
  Select,
  Combobox,
  ComboboxMultiple,
  Date,
  GroupRadio,
  Editor,
  UploadImage,
  UploadAvatar,
  Password,
  DateTimePicker,
}
export enum TypeDisplayEnum {
  /**
   * hiển thị hình ảnh trên grid
   */
  DisplayImage,

  /**
   * hiển thị dấu tích ✓ trên grid
   */
  Toggle,

  /**
   * Chỉ hiển thị dạng ngày
   */
  Date,

  /**
   * Chỉ hiển thị dạng ngày + giờ
   */
  DisplayDateTime,

  /**
   * Chỉ hiển thị dạng link tích hợp target='_blank'
   */
  DisplayLink,

  /**
   * Cho phép nhập điểm dạng text ngay trên grid
   */
  InputPointText,

  /**
   * Cho phép nhập điểm dạng số ngay trên grid **(Max 10)**
   */
  InputPointNumber,

  /**
   * select dạng có search
   */
  InputCombobox,

  /**
   * Cho phép nhập ngày ngay trên grid
   */
  InputDate,

  /**
   * Nhập select k có search
   */
  InputSelect,

  /**
   * Checkbox
   */
  InputCheckbox,

  /**
   * Dạng toggle input
   */
  InputToggle,
  /**
   * Cho phép nhập dạng số ngay trên grid
   */
  InputNumber,
  /**
   * Cho phép nhập dạng text ngay trên grid
   */
  InputText,
}
export interface ISysTableRequest extends IPagingRequestModel {
  // [key: string]: unknown;
}
export enum TypeInputFilterEnum {
  Text,
  Toggle,
  Select,
  Combobox,
  Date,
}

export interface IFilter<
  T extends AppSysTableType["FilterModel"] = AppSysTableDefaultType["FilterModel"]
> {
  /**
   * Tên field **Required** phải trùng với filter params
   */
  field: keyof T;

  /**
   * Label hiển thị tên của filter
   */
  label?: string;

  /**
   * Các type filter cơ bản đã được định nghĩa component sẵn
   */
  typeInput?: TypeInputFilterEnum;

  // TODO: làm sau
  // renderInput?: () => ReactNode;

  /**
   * - Truyền vào để cấu hình disable
   *
   * Mặc định **false**
   */
  isDisabled?: boolean;

  /**
   * - Đối với typeInput=select|Combobox truyền options là các giá trị của select để người dùng chọn
   *
   * *Nếu truyền cả options và getOptions sẽ ưu tiên lấy giá trị options*
   */
  options?: AppOptionType[];

  /**
   * - Đối với typeInput=select|Combobox hàm getOptions từ api để lấy các giá trị của select để người dùng chọn
   *
   * *Nếu truyền cả options và getOptions sẽ ưu tiên lấy giá trị options*
   */
  getOptions?: (props: {
    filter: T;
    /**
     * - Dùng để set disable cho các field của filter
     *
     * Ví dụ: setDisabled(["title", "name"]);
     */
    setDisabled: (value: (keyof T)[]) => void;

    /**
     * - Dùng để set bỏ đi trạng thái disable cho các field của filter
     *
     * Ví dụ: removeDisabled(["title", "name"]);
     */
    removeDisabled: (value: (keyof T)[]) => void;

    /**
     * - Dùng để set lại giá trị cho các field khác của filter nếu cần
     */
    setFilter: (value: { [key in keyof T]?: T[key] }) => void;

    /**
     * - Dùng để get lại giá trị option của filter với các loại select
     *
     * Ví dụ: setReGetOption(["combobox", "comboboxMultiple"])
     */
    setReGetOption: (value: (keyof T)[]) => void;
  }) => Promise<AppOptionType[]>;

  /**
   * - Đối với typeInput=select|Combobox sẽ thêm giá trị select all vào options
   *
   *  default= false
   */
  isHasSelectAll?: boolean;

  /**
   * - Đối với typeInput=select|Combobox nếu true sẽ load hàm getOptions xong thì mới load list
   *
   *  default= false
   */
  isRequireLoadedBeforeGetList?: boolean;

  /**
   * Hàm này chạy khi value của filter bị thay đổi
   */
  callBackInputChange?: (props: {
    /**
     * Đây là các keys và values của filter hiện tại
     */
    filter: T;

    /**
     * - Dùng để set disable cho các field của filter
     *
     * Ví dụ: setDisabled(["title", "name"]);
     */
    setDisabled: (value: (keyof T)[]) => void;

    /**
     * - Dùng để set bỏ đi trạng thái disable cho các field của filter
     *
     * Ví dụ: removeDisabled(["title", "name"]);
     */
    removeDisabled: (value: (keyof T)[]) => void;

    /**
     * - Dùng để set lại giá trị cho các field khác của filter nếu cần
     */
    setFilter: (value: { [key in keyof T]?: T[key] }) => void;

    /**
     * - Dùng để get lại giá trị option của filter với các loại select
     *
     * Ví dụ: setReGetOption(["combobox", "comboboxMultiple"])
     */
    setReGetOption: (value: (keyof T)[]) => void;
  }) => void;

  renderFilter?: (props: RenderFilterProps) => ReactNode;
  fromLabelFieldProps?: Pick<
    AppFromLabelFieldProps,
    "className" | "childrenWrapperProps"
  >;
  /**
   * cho phép hiển thị ở trên filter addvanced
   *
   *  default= false
   */
  isShowInFilterAdvanced?: boolean;

  /**
   * không hiển thị ở trên filter thường
   *
   *  default= false
   */
  isHideInFilter?: boolean;
}

export interface IControlForm<
  T extends
    | AppSysTableType["CreateModel"]
    | AppSysTableType["UpdateModel"] = ISysTableDefaultCreateUpdateModel
> {
  /**
   * - Tên field **Required** phải trùng với field model  Api để làm payload
   */
  field: keyof T;
  /**
   * - Label form **Required** hiển thị tên của input
   */
  label: string | ReactNode;

  /**
   * - validation rule sử dụng react hook form
   */
  rule?: RegisterOptions<FieldValues>;

  /**
   * - Set labelRequired=true để label thêm dấu * và thành mục bắt buộc phải nhập
   *
   * - Default ***false***
   */
  labelRequired?: boolean;

  /**
   * - Truyền vào để tạo ra nội dung của ghi chú cho input form
   */
  helper?: string | ReactNode;

  /**
   * Các type input form cơ bản đã được định nghĩa component sẵn
   *
   * Trường hợp có cả typeInput và renderInput thì ưu tiên typeInput
   */
  typeInput?: TypeInputEnum;

  /**
   * Có thể render input tùy chỉnh khác với các type input form cơ bản
   *
   * Trường hợp có cả typeInput và renderInput thì ưu tiên typeInput
   */
  renderInput?: (props: RenderFormControlProps<T>) => ReactNode;

  /**
   * - Đối với typeInput=select|Combobox truyền options là các giá trị của select để người dùng chọn
   *
   * *Nếu truyền cả options và getOptions sẽ ưu tiên lấy giá trị options*
   */
  options?: AppOptionType[];

  /**
   * - Đối với typeInput=select|Combobox hàm getOptions để lấy các giá trị từ api của select để người dùng chọn
   *
   * *Nếu truyền cả options và getOptions sẽ ưu tiên lấy giá trị options*
   */
  getOptions?: (props: { values: T }) => Promise<AppOptionType[]>;

  /**
   * - Truyền vào để cấu hình disable
   *
   * Mặc định **false**
   */
  isDisabled?: boolean;

  /**
   * - Dùng để chia column colSpan-grid trong form create hoặc edit (max 12)
   *
   * default 12
   */
  colForm?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

  /**
   * Hàm này chạy khi value của input form bị thay đổi
   */
  callBackInputChange?: (props: {
    /**
     * Đây là object các keys và values của input form hiện tại
     */
    values: unknown;

    /**
     * - Dùng để set disable cho các field của input form
     *
     * Ví dụ: setDisabled(["title", "name"]);
     */
    setDisabled: (value: (keyof T)[]) => void;

    /**
     * - Dùng để set bỏ đi trạng thái disable cho các field của input form
     *
     * Ví dụ: removeDisabled(["title", "name"]);
     */
    removeDisabled: (value: (keyof T)[]) => void;

    /**
     * - Dùng để set lại các giá trị input field của form nếu cần
     *
     * ví dụ setValue("field1", "giá trị của field1")
     */
    setValue: UseFormSetValue<{ [key in keyof T]?: T[key] }>;

    /**
     * - Dùng để get lại giá trị option của input form với các loại select
     *
     * Ví dụ: setReGetOption(["fieldSelect1", "fieldSelect2"])
     */
    setReGetOption: (value: (keyof T)[]) => void;

    /**
     * - Dùng để lấy giá trị reponse của api upload
     */
    filesUploaded?: IUploadFile;
  }) => void;
  formControlProps?: SysTableFormControlProps;
}
export enum TypeTableEnum {
  Default,
  Tanstack,
}
export type RenderFormControlProps<
  T extends
    | AppSysTableType["CreateModel"]
    | AppSysTableType["UpdateModel"] = ISysTableDefaultCreateUpdateModel
> = {
  control: Control<T, unknown>;
  formState: Pick<FormState<T>, "errors" | "isSubmitting">;
  handleSubmit: UseFormHandleSubmit<T, undefined>;
  setValue: UseFormSetValue<{ [key in keyof T]?: T[key] }>;
  getValues: UseFormGetValues<T>;
  trigger: UseFormTrigger<{ [key in keyof T]?: T[key] }>;
  watch: UseFormWatch<T>;
  dataInit?: { [key in keyof T]?: T[key] };
};
export type SysTableFormControlProps =
  | Omit<TextFieldProps, "onChange">
  | Omit<SwitchProps, "onChange">
  | Omit<DatePickerProps<dayjs.Dayjs>, "onChange" | "slotProps" | "slots">
  | Omit<SelectProps, "onChange" | "options">
  | Omit<
      AppComboboxProps,
      "onChange" | "options" | "multiple" | "isOptionEqualToValue"
    >
  | Omit<AppRadioGroupProps, "options" | "onChange">;
export const getColumnsDisplay = (columns: IColumn[]) => {
  return columns.filter((c) => !c.isHideGrid);
};
export enum TypeKeyDowmEnum {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
}
export const renderIdInputGrid = (field: string, index: number) => {
  return `jsInputGrid_${field}_${index}`;
};
export interface IColumnPinning {
  left?: string[];
  right?: string[];
}
export interface IColumnConvert<T extends AppSysTableType["RowModel"]>
  extends Omit<IColumn<T>, "children"> {
  depth: number;
  colSpan: number;
  rowSpan: number;
  parentId: string;
  left?: number;
  right?: number;
  isLast: boolean;
  sticky: boolean;
}
export const renderHeaderTable = <T extends AppSysTableType["RowModel"]>(
  columns: IColumn<T>[],
  columnPinning?: IColumnPinning
) => {
  const columnLast: IColumnConvert<T>[] = [];
  const columnsConvert = columns.map((column) => {
    const newColumn: IColumnConvert<T>[][] = [];
    renderColumnHeader(column, 0, newColumn, columnLast);
    return newColumn;
  });
  const maxDep = Math.max(...columnsConvert.map((c) => c.length)) - 1;
  if (columnPinning && columnPinning.left && columnPinning.left.length > 0) {
    let left = 0;
    columnLast
      .filter((c) => columnPinning.left?.includes(c.field as string))
      .forEach((column) => {
        column.left = left;
        left = left + (column.tableProps?.size || 150);
        column.sticky = true;
      });
  }
  if (columnPinning && columnPinning.right && columnPinning.right.length > 0) {
    let right = 0;
    columnLast
      .filter((c) => columnPinning.right?.includes(c.field as string))
      .reverse()
      .forEach((column) => {
        column.right = right;
        right = right + (column.tableProps?.size || 150);
        column.sticky = true;
      });
  }
  const columnsHeader: IColumnConvert<T>[][] = [];
  columnsConvert.forEach((col, index) => {
    const colReverse = col.reverse();
    const lengthReverse = colReverse.length;
    colReverse.forEach((c1, index1) => {
      let indexChild = index1 > 0 ? index1 - 1 : undefined;
      c1.forEach((c2, index2) => {
        if (c2.isLast) {
          c2.rowSpan = maxDep - c2.depth + 1;
        }
        if (indexChild !== undefined) {
          const listChilds = colReverse[indexChild].filter(
            (c) => c.parentId === c2.field
          );
          if (
            columnPinning &&
            columnPinning.left &&
            columnPinning.left.length > 0 &&
            columnPinning.left.includes(c2.field as string) &&
            listChilds.length > 0
          ) {
            c2.left = listChilds[0].left;
            c2.sticky = true;
          }
          if (
            columnPinning &&
            columnPinning.right &&
            columnPinning.right.length > 0 &&
            columnPinning.right.includes(c2.field as string) &&
            listChilds.length > 0
          ) {
            c2.right = listChilds[listChilds.length - 1].right;
            c2.sticky = true;
          }
          c2.colSpan =
            listChilds.reduce(
              (accumulator, currentValue) => accumulator + currentValue.colSpan,
              0
            ) || 1;
        }
      });
      if (!columnsHeader?.[lengthReverse - 1 - index1])
        columnsHeader[lengthReverse - 1 - index1] = [];
      columnsHeader[lengthReverse - 1 - index1].push(...c1);
    });
  });
  return {
    listColumnsHeader: columnsHeader,
    listColumnsLast: columnLast,
  };
};
const renderColumnHeader = <T extends AppSysTableType["RowModel"]>(
  column: IColumn<T>,
  dep: number = 0,
  res: IColumnConvert<T>[][] = [],
  resLast: IColumnConvert<T>[] = [],
  parentId: string = ""
) => {
  const depth = dep;
  if (!res?.[depth]) res[depth] = [];
  const isLast = !(column.children && column.children?.length > 0);

  const newColumn = {
    ...column,
    depth: depth,
    colSpan: 1,
    rowSpan: 1,
    parentId: parentId,
    isLast: isLast,
    sticky: false,
  };

  delete newColumn.children;
  if (isLast) {
    resLast.push(newColumn);
    if (newColumn.tableProps) {
      if (!newColumn.tableProps.size) newColumn.tableProps.size = 150;
    } else {
      newColumn.tableProps = {
        size: 150,
      };
    }
  }
  res[depth].push(newColumn);
  column.children?.forEach((c) => {
    renderColumnHeader(c, dep + 1, res, resLast, column.field as string);
  });
};
export const getSxGridHeaderTable = <T extends AppSysTableType["RowModel"]>(
  column: IColumnConvert<T>
) => {
  const res: SxProps<Theme> = {
    width:
      column.tableProps?.size !== undefined
        ? `${column.tableProps?.size}px`
        : undefined,
    minWidth:
      column.tableProps?.minSize !== undefined
        ? `${column.tableProps?.minSize}px`
        : undefined,
    maxWidth:
      column.tableProps?.maxSize !== undefined
        ? `${column.tableProps?.maxSize}px`
        : undefined,
  };

  return {
    ...res,
    ...getSxGridBodyTable<T>(column),
  };
};
export const getSxGridBodyTable = <T extends AppSysTableType["RowModel"]>(
  column: IColumnConvert<T>
) => {
  if (column.sticky) {
    const sx: SxProps<Theme> = {
      position: "sticky",
      left: column.left,
      right: column.right,
      width: `${column.tableProps?.size || 150}px`,
      minWidth: `${column.tableProps?.size || 150}px`,
      maxWidth: `${column.tableProps?.size || 150}px`,
      zIndex: 1,
    };
    return sx;
  } else return {};
};
export interface IColumnPinningState {
  left?: string[];
  right?: string[];
}
export const KEY_DEFAULT = {
  ORDER: "_index",
  CHECKBOX: "_checkbox",
  ACTION: "_action",
};

export type RenderFilterProps = {
  handleChangeInputDefault: (value: string) => void;
  filter: ISysTableRequest;
  /**
   * - Dùng để set disable cho các field của filter
   *
   * Ví dụ: setDisabled(["title", "name"]);
   */
  setDisabled: (value: string[]) => void;

  /**
   * - Dùng để set bỏ đi trạng thái disable cho các field của filter
   *
   * Ví dụ: removeDisabled(["title", "name"]);
   */
  removeDisabled: (value: string[]) => void;

  /**
   * - Dùng để set lại giá trị cho các field khác của filter nếu cần
   */
  setFilter: (value: { [key: string]: unknown }) => void;

  /**
   * - Dùng để get lại giá trị option của filter với các loại select
   *
   * Ví dụ: setReGetOption(["combobox", "comboboxMultiple"])
   */
  setReGetOption: (value: string[]) => void;
};

export type AppSysTableProps<
  T extends AppSysTableType = AppSysTableDefaultType
> = {
  /**
   *  class name table
   *
   */
  className?: string;

  /**
   * Danh sách cột trong table ***Required**.
   */
  columns: IColumn<T["RowModel"], T["FilterModel"]>[];

  /**
   * API URL với method ***GET*** để lấy danh sách table chính ***Required**.
   */
  apiUrlList?: (() => string) | string;
  /**
   * Hàm handleUpdate để xử lý khi call ***GET*** danh sách, nếu truyền apiUrlList thì hàm này sẽ được bỏ qua và ưu tiên apiUrlList
   *
   * *Trả về *
   */
  handleGetList?: (props: HandleGetListPropsType<T>) => void;
  /**
   * API URL với method ***GET*** để lấy chi tiết theo id (get by Id).
   *
   * Endpoint API lấy chi tiết theo id **không cần thêm path param {id}** (sẽ tự động được thêm)
   */
  apiUrlGetDetailUpdate?: ((row: T["RowModel"]) => string) | string;

  /**
   * API URL với method ***PUT*** để chỉnh sửa theo **id** một row đã chọn.
   */
  apiUrlUpdate?: ((id: string) => string) | string;

  /**
   * Hàm handleUpdate để xử lý khi call ***PUT*** chỉnh sửa, nếu truyền apiUrlUpdate thì hàm này sẽ được bỏ qua và ưu tiên apiUrlUpdate
   *
   * *Trả về "" nếu thành công hoặc lỗi trả về messeage lỗi*
   */
  handleUpdate?: (data: T["UpdateModel"], id: unknown) => Promise<string>;

  /**
   * API URL với method ***POST*** để thêm mới.
   */
  apiUrlCreate?: (() => string) | string;

  /**
   * Hàm handleCreate để xử lý khi call ***POST*** thêm mới, nếu truyền apiUrlCreate thì hàm này sẽ được bỏ qua và ưu tiên apiUrlCreate
   *
   * *Trả về "" nếu thành công hoặc lỗi trả về messeage lỗi*
   */
  handleCreate?: (data: T["CreateModel"]) => Promise<string>;

  /**
   * API URL với method ***DELETE*** để xóa một row.
   * Endpoint API xóa danh mục
   * kiểu string  hoặc function return string
   *
   * Nếu truyền string giá trị api khi call sẽ tự động thêm /{id} ví dụ truyền "/example/delete" => url call api = "/example/delete/{id}"
   * Nếu truyền function giá trị url call api sẽ là giá trị của function
   */
  apiUrlDelete?: ((row: T["RowModel"]) => string) | string;

  /**
   * Hàm handleDelete để xử lý khi call ***DELETE*** xóa, nếu truyền apiUrlDelete thì hàm này sẽ được bỏ qua và ưu tiên apiUrlDelete
   *
   * *Trả về "" nếu thành công hoặc lỗi trả về messeage lỗi*
   */
  handleDelete?: (id: string) => Promise<string>;

  /**
   * Set cột gắn cố định bên trái hoặc bên phải khi scroll
   */
  columnPinning?: IColumnPinningState;

  /**
   * Set cố định bên trái của cột **STT**
   *
   * *Default false*
   */
  isLockColumnOrder?: boolean;

  /**
   * Set cố định bên trái của cột **Checkbox**
   */
  isLockColumnCheckbox?: boolean;

  /**
   * Set cố định bên trái của cột **Thao tác**
   */
  isLockColumnAction?: boolean;

  /**
   * Set giá trị khởi tạo cho filter
   */
  initFilter?: {
    [key in keyof T["FilterModel"]]?: T["FilterModel"][key];
  };

  /**
   * Set danh sách các input có trong popup **Thêm mới**
   */
  controlFormCreate?: IControlForm<T["CreateModel"]>[];

  /**
   * set giá trị mặc định lúc đầu form thêm mới (set default value form)
   */
  dataInitCreate?: {
    [key in keyof T["CreateModel"]]?: T["CreateModel"][key];
  };

  /**
   * Component truyền vào để tạo nội dung popup **Thêm mới**
   *
   * *Dùng trong trường hợp có những form đặc thù*
   */
  renderFormCreate?: (props: RenderFormCreateProps<T>) => ReactNode;

  /**
   * Set danh sách các input có trong popup **Chỉnh sửa**
   */
  controlFormUpdate?: IControlForm<T["UpdateModel"]>[];

  /**
   * Component truyền vào để tạo nội dung popup **Chỉnh sửa**
   *
   * *Dùng trong trường hợp có những form đặc thù*
   */
  renderFormUpdate?: (props: RenderFormUpdateProps<T>) => ReactNode;

  /**
   * Hiển thị button cấu hình ẩn/hiện cột
   */
  isCanSetupColumn?: boolean;

  /**
   * Hiển thị button ẩn/hiện collapse filter nằm trên table header
   */
  isCanToggleFilter?: boolean;

  /**
   * Set danh sách các dạng type input filter **cơ bản** nằm trên table header
   */
  listFilter?: IFilter<T["FilterModel"]>[];

  /**
   * Component truyền vào các dạng type input filter **tự tùy chỉnh/đặc thù** nằm trên table header
   */
  filterComponent?: ReactNode;

  /**
   * Component truyền vào để tạo các filter trong popup **Bộ lọc nâng cao**
   */
  renderFilterAdvanced?: (
    props: RenderFilterAdvancedProps<T["FilterModel"]>
  ) => ReactNode;

  /**
   * Hiển thị button ẩn/hiện ghi chú
   */
  isCanToggleNote?: boolean;

  /**
   * Component truyền vào để tạo nội dung cho ghi chú
   *
   * *Thường đi kèm với **isCanToggleNote***
   */
  noteComponent?: ReactNode;

  /**
   * Component truyền vào các action riêng của page đặc thù
   *
   * *Các action này sẽ nằm bên trái button "Thêm mới" góc trên bên phải màn hình*
   */
  action?:
    | ((
        useSysTableSelector: UseSysTableSelectorType<
          T["RowModel"],
          T["FilterModel"]
        >,
        sysTableActions: ISysTableAction<T["RowModel"], T["FilterModel"]>
      ) => ReactNode)
    | ReactNode;

  /**
   * Component truyền vào các action riêng của page đặc thù
   *
   *  *Các action này sẽ nằm góc trên bên trái màn hình*
   */
  moreHeaderLeftAction?: ReactNode;

  /**
   * Hàm handleDelete để xử lý xuất excel
   *
   * *Trả về "" nếu thành công hoặc lỗi trả về messeage lỗi*
   */
  handleExportExcel?: (filter: T["FilterModel"]) => Promise<string>;

  // handleGetDetailUpdate để xử lý call get chi tiết row update , nếu truyền apiUrlGetDetail thì hàm này sẽ được bỏ qua và ưu tiên apiUrlGetDetail
  handleGetDetailUpdate?: (
    id: string
  ) => Promise<IDataResponseModel<T["DetailUpdateModel"]>>;

  /**
   * url trang danh sách
   */
  urlList?: string;

  /**
   * Truyền vào tên field của column để lấy được giá trị của row đó hiển thị trên delete popup confirm
   */
  fieldTitleDelete?: keyof T["RowModel"];

  /**
   * Hiển thị icon **chỉnh sửa** trong cột thao tác
   *
   * *Mặc định không truyền sẽ là **True***
   */
  isCanUpdate?: boolean;

  /**
   * Hiển thị icon **xóa** trong cột thao tác
   *
   * *Mặc định không truyền sẽ là **True***
   */
  isCanDelete?: boolean;

  /**
   * Hiển thị nút thêm mới trên góc phải màn hình
   *
   * *Mặc định không truyền sẽ là **True***
   */
  isCanCreate?: boolean;

  /**
   * Hiển thị nút xuất excel trên góc phải màn hình
   *
   * *Mặc định không truyền sẽ là **True***
   */
  isExportExcel?: boolean;

  /**
   * Hiển thị cột Checkbox
   *
   * *Mặc định không truyền sẽ là **True***
   */
  isCanCheckbox?: boolean;

  /**
   * Tạo thêm action ở bên trong cột Thao tác cho những trường hợp đặc biệt
   */
  moreRowAction?: {
    size: number;
    render: (value: T["RowModel"], index: number) => string | ReactNode;
  };
  renderActionOfCheckbox?: (props: {
    checkedList: T["RowModel"][];
    setCheckedList: (listCheckedId: string[]) => void;
    useSysTableSelector: UseSysTableSelectorType<
      T["RowModel"],
      T["FilterModel"]
    >;
    sysTableActions: ISysTableAction<T["RowModel"], T["FilterModel"]>;
  }) => ReactNode;

  /**
   * API URL với method ***GET*** để lấy chi tiết theo id (get by Id).
   *
   * Endpoint API lấy chi tiết theo id **không cần thêm path param {id}** (sẽ tự động được thêm)
   */
  apiUrlGetDetail?: string;

  // handleGetDetailUpdate để xử lý call get chi tiết row update , nếu truyền apiUrlGetDetail thì hàm này sẽ được bỏ qua và ưu tiên apiUrlGetDetail
  handleGetDetail?: (
    id: unknown
  ) => Promise<IDataResponseModel<T["DetailModel"]>>;
  /**
   * Hiển thị nút chi tiet
   *
   * *Mặc định không truyền sẽ là **false***
   */
  isCanDetail?: boolean;
  renderDetail?: (props: {
    isOpen: boolean;
    handleClose: () => void;
    dataDetail: T["DetailModel"] | undefined;
  }) => ReactNode;
  categoryType?: CategoryTypeUploadEnum;

  notification?: {
    insertSuccess?: string | ReactNode;
    updateSuccess?:
      | string
      | ReactNode
      | ((row: T["RowModel"]) => string | ReactNode);
    deleteSuccess?:
      | string
      | ReactNode
      | ((row: T["RowModel"]) => string | ReactNode);
    changeStatusSuccess?:
      | string
      | ReactNode
      | ((row: T["RowModel"]) => string | ReactNode);
  };
};

export type RenderFilterAdvancedProps<
  FilterModel extends AppSysTableType["FilterModel"] = ISysTableDefaultFilterModel
> = {
  setFilter: (data: { [key in keyof FilterModel]?: FilterModel[key] }) => void;
  setDisabled: (value: (keyof FilterModel)[]) => void;
  removeDisabled: (value: (keyof FilterModel)[]) => void;
  setOption: (data: { [key in keyof FilterModel]?: AppOptionType[] }) => void;
  listFilter?: IFilter<FilterModel>[];
  handleCloseModal: () => void;
  filter: FilterModel;
  options: {
    [key in keyof FilterModel]: AppOptionType[];
  };
};
export type RenderFormCreateProps<
  T extends AppSysTableType = AppSysTableDefaultType
> = {
  isOpen: boolean;
  handleClose: () => void;
  useSysTableSelector: UseSysTableSelectorType<T["RowModel"], T["FilterModel"]>;
  sysTableActions: ISysTableAction<T["RowModel"], T["FilterModel"]>;
};
export type RenderFormUpdateProps<
  T extends AppSysTableType = AppSysTableDefaultType
> = RenderFormCreateProps<T> & {
  dataInit: T["DetailUpdateModel"];
  idUpdate: string;
};
export type AppSysTableType<
  RowModel extends ISysTableItem = ISysTableItem,
  FilterModel extends ISysTableRequest = ISysTableRequest,
  CreateModel extends {} = {},
  UpdateModel extends {} = {},
  DetailUpdateModel extends {} = {},
  DetailModel extends {} = {}
> = {
  RowModel: RowModel;
  FilterModel: FilterModel;
  CreateModel: CreateModel;
  UpdateModel: UpdateModel;
  DetailUpdateModel: DetailUpdateModel;
  DetailModel: DetailModel;
};

// export interface ISysTableFilter extends IPagingRequestModel {}
export interface ISysTableDefaultItem extends ISysTableItem {
  [key: string]: unknown;
}

export interface ISysTableDefaultFilterModel extends ISysTableRequest {
  [key: string]: unknown;
}
export interface ISysTableDefaultCreateUpdateModel {
  [key: string]: unknown;
}
export interface ISysTableDefaultDetailModel {
  [key: string]: unknown;
}
export interface ISysTableDefaultDetailUpdateModel {
  [key: string]: unknown;
}
export type AppSysTableDefaultType = AppSysTableType<
  ISysTableDefaultItem,
  ISysTableDefaultFilterModel,
  ISysTableDefaultCreateUpdateModel,
  ISysTableDefaultCreateUpdateModel,
  ISysTableDefaultDetailUpdateModel,
  ISysTableDefaultDetailModel
>;
